import React, { useState, useContext, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MakeBidDialogue from "./make-bid-dialogue";
import PremPlayersContext from "../../contexts/premplayers-context";
import SettledWindowTransactionsContext from "../../contexts/settled-window-transactions-context";
import UserPendingTransactionsContext from "../../contexts/user-pending-transactions-context";
import { LeaderboardContext } from "../../contexts/leaderboard-context";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";

export const MakeBid = ({ setNavValue }) => {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { getPlayerPositionById, getTeamById, getCurrentWindow } = useSiteVarsFunctions();

  const [isMakeBidDialogueOpen, setMakeBidDialogueIsOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState({});
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);
  const { leaderboardData } = useContext(LeaderboardContext);
  const { settledWindowTransactionsData, isLoadingSettledWindowTransactionsData } = useContext(SettledWindowTransactionsContext);
  const { userPendingTransactionsData, isLoadingUserPendingTransactionsData } = useContext(UserPendingTransactionsContext);

  const isLoading = isLoadingPremPlayersData || isLoadingSettledWindowTransactionsData || isLoadingUserPendingTransactionsData;

  // Filter out the players which are already owned by a club or already bought in this window
  const availablePlayers = useMemo(() => {
    if (!premPlayersData || premPlayersData.length === 0) return [];

    let ownedPlayers = [];
    let filteredOwnedPlayers = [];
    for (let i = 0; i < leaderboardData.length; i++) {
      if (leaderboardData[i].players) {
        ownedPlayers = ownedPlayers.concat(leaderboardData[i].players);
      }
    }

    for (let i = 0; i < ownedPlayers.length; i++) {
      if (ownedPlayers[i].transfer !== "in") {
        filteredOwnedPlayers.push(ownedPlayers[i].playerId);
      }
    }

    let unowned = premPlayersData.filter(player => !filteredOwnedPlayers.includes(player.playerId));

    let purchasedPlayers = [];
    const settledItems = settledWindowTransactionsData?.Items || [];
    for (let i = 0; i < settledItems.length; i++) {
      const purchasedPlayer = settledItems[i];
      if (purchasedPlayer.status === "win") {
        purchasedPlayers.push(purchasedPlayer.playerId);
      }
    }

    return unowned.filter(player => !purchasedPlayers.includes(player.playerId));
  }, [leaderboardData, premPlayersData, settledWindowTransactionsData]);

  // Count available players per position
  const positionCounts = useMemo(() => {
    const counts = { all: availablePlayers.length, 1: 0, 2: 0, 3: 0, 4: 0 };
    availablePlayers.forEach(p => {
      const posId = Number(p.positionId);
      if (counts[posId] !== undefined) {
        counts[posId]++;
      }
    });
    return counts;
  }, [availablePlayers]);

  // Filter and sort players based on position filter and search query
  const displayedPlayers = useMemo(() => {
    let list = availablePlayers;

    // Filter by position
    if (selectedPosition !== 'all') {
      list = list.filter(p => Number(p.positionId) === Number(selectedPosition));
    }

    // Filter by search query
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(p => {
        const team = getTeamById(p.teamId);
        const pos = getPlayerPositionById(p.positionId);
        const lastName = (p.lastName || '').toLowerCase();
        const firstName = (p.firstName || '').toLowerCase();
        const webName = (p.webName || p.web_name || '').toLowerCase();
        const teamName = (team?.name || '').toLowerCase();
        const teamShort = (team?.name_short || '').toLowerCase();
        const posName = (pos?.name || '').toLowerCase();
        const posShort = (pos?.name_short || '').toLowerCase();

        return (
          lastName.includes(term) ||
          firstName.includes(term) ||
          webName.includes(term) ||
          teamName.includes(term) ||
          teamShort.includes(term) ||
          posName.includes(term) ||
          posShort.includes(term)
        );
      });
    }

    // Sort by position ascending, then originalCost descending, then totalPoints descending
    return [...list].sort((a, b) => {
      if (a.positionId !== b.positionId) {
        return a.positionId - b.positionId;
      }
      if (a.originalCost !== b.originalCost) {
        return b.originalCost - a.originalCost;
      }
      return b.totalPoints - a.totalPoints;
    });
  }, [availablePlayers, selectedPosition, searchTerm, getTeamById, getPlayerPositionById]);

  // Pending transactions and window bid count
  const { pendingTransactions, windowBidCount } = useMemo(() => {
    const pending = [];
    let bidCount = 0;
    const currentWin = getCurrentWindow();
    const items = userPendingTransactionsData?.Items || [];

    for (let i = 0; i < items.length; i++) {
      const pendingTransaction = items[i];
      if (pendingTransaction.window === currentWin) {
        if (pendingTransaction.status === "pending") {
          pending.push(pendingTransaction.playerId);
          bidCount++;
        } else if (pendingTransaction.status === "win") {
          bidCount++;
        }
      }
    }
    return { pendingTransactions: pending, windowBidCount: bidCount };
  }, [userPendingTransactionsData, getCurrentWindow]);

  const getPositionFilterLabel = (posId) => {
    switch (Number(posId)) {
      case 1: return 'Goalkeepers';
      case 2: return 'Defenders';
      case 3: return 'Midfielders';
      case 4: return 'Forwards';
      default: return 'All Positions';
    }
  };

  const handleResetFilters = () => {
    setSelectedPosition('all');
    setSearchTerm('');
  };

  return (
    <Box sx={{ width: '100%', mb: 5 }}>
      {isMakeBidDialogueOpen && (
        <MakeBidDialogue
          open={isMakeBidDialogueOpen}
          playerId={selectedPlayerData.playerId}
          handleCloseMakeBidDialogue={setMakeBidDialogueIsOpen}
          setNavValue={setNavValue}
        />
      )}

      {/* Filter and Search Controls */}
      <Box sx={{ mb: 2, px: { xs: 0.5, sm: 0 } }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by player or club name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end" aria-label="Clear search">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 1.5, bgcolor: 'background.paper' }}
        />

        <ToggleButtonGroup
          value={selectedPosition}
          exclusive
          onChange={(_, newPos) => {
            if (newPos !== null) {
              setSelectedPosition(newPos);
            } else {
              setSelectedPosition('all');
            }
          }}
          disabled={isLoading}
          size="small"
          color="primary"
          fullWidth
          sx={{
            bgcolor: 'background.paper',
            display: 'flex',
            '& .MuiToggleButton-root': {
              py: 0.75,
              px: { xs: 0.5, sm: 1.5 },
              fontSize: { xs: '0.72rem', sm: '0.82rem' },
              fontWeight: 'bold',
              textTransform: 'none',
              flex: 1,
            }
          }}
        >
          <ToggleButton value="all">
            All ({positionCounts.all})
          </ToggleButton>
          <ToggleButton value={1}>
            {isSmUp ? 'Goalkeepers' : 'GKP'} ({positionCounts[1] || 0})
          </ToggleButton>
          <ToggleButton value={2}>
            {isSmUp ? 'Defenders' : 'DEF'} ({positionCounts[2] || 0})
          </ToggleButton>
          <ToggleButton value={3}>
            {isSmUp ? 'Midfielders' : 'MID'} ({positionCounts[3] || 0})
          </ToggleButton>
          <ToggleButton value={4}>
            {isSmUp ? 'Forwards' : 'FWD'} ({positionCounts[4] || 0})
          </ToggleButton>
        </ToggleButtonGroup>

        {(selectedPosition !== 'all' || searchTerm.trim() !== '') && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{displayedPlayers.length}</strong> player{displayedPlayers.length === 1 ? '' : 's'}
              {selectedPosition !== 'all' && ` in ${getPositionFilterLabel(selectedPosition)}`}
              {searchTerm.trim() && ` matching "${searchTerm.trim()}"`}
            </Typography>
            <Button
              size="small"
              onClick={handleResetFilters}
              sx={{ fontSize: '0.75rem', py: 0, textTransform: 'none' }}
            >
              Clear filters
            </Button>
          </Box>
        )}
      </Box>

      {/* Table Container */}
      <TableContainer component={Paper}>
        {isLoading && (
          <Stack spacing={1}>
            <Skeleton variant="rounded" width={"100%"} height={30} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
            <Skeleton variant="rounded" width={"100%"} height={26} />
          </Stack>
        )}

        {!isLoading && (
          <Table sx={{ minWidth: 390 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ paddingLeft: 6, paddingRight: 0 }}></TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Player</TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Cost</TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Points</TableCell>
                <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedPlayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No available players match your search or filter criteria.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleResetFilters}
                      sx={{ textTransform: 'none' }}
                    >
                      Reset Filters
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedPlayers.map((row) => (
                  <TableRow key={row.playerId}>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 0 }}>
                      <img
                        src={`https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${getTeamById(row.teamId).kitId}${getPlayerPositionById(row.positionId).name_short === "GKP" ? "_1" : ""}-66.webp`}
                        alt=""
                        style={{ width: '40px', height: '40px' }}
                      />
                    </TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>
                      <span>{row.lastName}</span>
                      <br />
                      <span style={{ fontWeight: 'bold' }}>
                        {getPlayerPositionById(row.positionId).name_short}
                      </span>{' '}
                      {getTeamById(row.teamId).name_short}
                    </TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>{row.originalCost}</TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>{row.totalPoints}</TableCell>
                    <TableCell style={{ paddingLeft: 6, paddingRight: 6 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={windowBidCount >= 4 || pendingTransactions.includes(row.playerId)}
                        onClick={() => {
                          setSelectedPlayerData(row);
                          setMakeBidDialogueIsOpen(true);
                        }}
                      >
                        Bid
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

